/**
 * savedShared.ts — shared trips a guest has saved for later ("Save" on a
 * /trip/shared page), kept on this device. Not a copy of the trip: a link
 * back to it, with enough to draw a row (name, days, km, a photo). The
 * planner's empty state lists them under "Trips you saved".
 */
export const SAVED_SHARED_KEY = 'tp-saved-shared-trips';
const MAX = 30;

export interface SavedShared {
  token: string;
  name: string;
  days?: number;
  km?: number;
  cover?: string | null;
  savedAt: number;
}

const validToken = (t: unknown): t is string => typeof t === 'string' && /^[\w-]{4,128}$/.test(t);

export function readSavedShared(local: Pick<Storage, 'getItem'> | null): SavedShared[] {
  if (!local) return [];
  try {
    const list = JSON.parse(local.getItem(SAVED_SHARED_KEY) || '[]');
    if (!Array.isArray(list)) return [];
    return list
      .filter((s) => validToken(s?.token) && typeof s?.name === 'string')
      .map((s) => ({
        token: s.token,
        name: String(s.name).slice(0, 120),
        ...(typeof s.days === 'number' ? { days: s.days } : {}),
        ...(typeof s.km === 'number' ? { km: s.km } : {}),
        cover: typeof s.cover === 'string' && /^https:\/\//.test(s.cover) ? s.cover : null,
        savedAt: typeof s.savedAt === 'number' ? s.savedAt : 0,
      }))
      .sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
}

export function isSavedShared(local: Pick<Storage, 'getItem'> | null, token: string): boolean {
  return readSavedShared(local).some((s) => s.token === token);
}

/** Save it, or unsave it if it's already saved. Returns whether it is saved now (null: storage blocked). */
export function toggleSavedShared(
  local: Pick<Storage, 'getItem' | 'setItem'> | null,
  entry: Omit<SavedShared, 'savedAt'>,
  now = Date.now()
): boolean | null {
  if (!local || !validToken(entry.token)) return null;
  const list = readSavedShared(local);
  const had = list.some((s) => s.token === entry.token);
  const next = had ? list.filter((s) => s.token !== entry.token) : [{ ...entry, savedAt: now }, ...list].slice(0, MAX);
  try {
    local.setItem(SAVED_SHARED_KEY, JSON.stringify(next));
    return !had;
  } catch {
    return null;
  }
}
